import {
    Box,
    Button,
    ChakraProvider,
    Flex,
    Grid,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Table,
    Tbody,
    List,
    ListItem,
    ListIcon,
    Td,
    Text,
    Textarea,
    Th,
    Thead,
    Tr,
    useDisclosure,
} from '@chakra-ui/react';
import {Next, PageGroup, Paginator, Previous} from 'chakra-paginator';
import {useAuth} from 'contexts/AuthContext';
import {navigate} from 'gatsby';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import ApiFetch from '../../utils/api';
import useNeedsMembership from "../../hooks/useNeedsMembership";
import Loader from "../../components/ui-components/Loader";
import {FormElement} from 'starterComponents'
import {Container} from 'starterUiComponents'
import {ChevronRightIcon} from "@chakra-ui/icons";
import {getRole, ROLES} from "../../utils/Roles";

const DEFAULT_PAGE_SIZE = 30;
const STATUSES = {
    FETCHING: 'fetching',
    SUCCESS: 'success',
    ERROR: 'error',
    SUBMITTING: 'submitting',
    SUBMITTED: 'submitted',
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'fetch':
            return {
                ...state,
                calls: [],
                total: 0,
                status: 'fetching',
                error: null,
                page: action.payload.page,
                // pageCount: 0,
                filter: action.payload.filter,
                sort: action.payload.sort,
            }
        case 'success':
            return {
                ...state,
                calls: action.payload.data,
                total: action.payload.count,
                status: 'success',
                error: null,
                pageCount: Math.ceil(action.payload.count / DEFAULT_PAGE_SIZE),
            };
        case 'error':
            return {
                ...state,
                status: 'error',
                error: action?.payload?.error ?? null,
            };
        case 'set_filter_by':
            return {
                ...state,
                filter: action.payload.filter,
            };
        case 'submitting':
            return {
                ...state,
                status: 'submitting',
            };
        case 'submitted':
            return {
                ...state,
                status: 'success',
            };
            case 'validationErrors':
            return {
                ...state,
                status: 'error',
                error: action.payload.validationErrors,
            };
        default:
            throw new Error(`CallHistory - Unssupported action type ${action.type}`);
    }
};

const CallHistoryPage = ({user}) => {
    const {currentUser, loading} = useAuth();
    const {isOpen, onOpen, onClose} = useDisclosure();
    const role = getRole(currentUser)
    const isRecycler = React.useMemo(() => role === ROLES.RECYCLER, [role])

    const [selectedCallGroup, setSelectedCallGroup] = useState(null);
    const [isRequestCallbackModal, setIsRequestCallbackModal] = useState(false);

    const newCommentsRef = useRef(null);

    const onGoToPage = async (page) => {
        await fetchCallHistory(page);
    }

    const tableRef = React.useRef(null);
    const rowRef = React.useRef(null);
    const containerRef = React.useRef(null);
    const fakeScrollRef = React.useRef(null);
    const [rowWidth, setRowWidth] = useState(null);
    const prevScrollX = useRef(0);

    let scrolling = false;
    const onScrollFake = (e) => {
        if (scrolling) {
            scrolling = false;
            return true;
        }
        scrolling = true;
        let goingRight = false;
        const currentScrollX = e.target.scrollLeft;
        if (prevScrollX.current < currentScrollX && goingRight) {
            goingRight = false;
        }
        if (prevScrollX.current > currentScrollX && !goingRight) {
            goingRight = true;
        }
        prevScrollX.current = currentScrollX;
        containerRef.current.scrollLeft = currentScrollX * (goingRight ? -1 : 1);
    };

    const onScrollReal = (e) => {
        if (scrolling) {
            scrolling = false;
            return true;
        }
        scrolling = true;
        fakeScrollRef.current.scrollLeft = containerRef.current.scrollLeft;
    };

    const [{calls, status, error, page, pageSize, pageCount}, dispatch] =
        React.useReducer(reducer, {
            status: 'idle',
            error: null,
            calls: [],
            total: 0,
            page: 1,
            pageSize: DEFAULT_PAGE_SIZE,
            pageCount: 1,
            sort: null,
            filter: null,
        });

    const fetchCallHistory = useCallback(
        async (page = 1, options = {}) => {
            const URL = `/techcalls`
            if (options.sort || options.filter) {
                page = 1;
            }
            dispatch({
                type: 'fetch',
                payload: {
                    page,
                    sort: options?.sort ?? null,
                    filter: options?.filter ?? null,
                },
            });
            try {
                const sub_num = currentUser?.shopData?.sub_num;
                let fetchUrl = `${URL}/shopsubnum/${sub_num}?page=${page}&pageSize=${pageSize}`;
                if (options.sort) {
                    fetchUrl += `&sort_by=${options.sort.order}${options.sort.sortBy}`;
                }
                if (options.filter) {
                    fetchUrl += `&${options.filter.filterKey}=${options.filter.filterValue}`;
                }

                const response = await ApiFetch({
                    path: fetchUrl,
                    userToken: currentUser?.shopData?.idToken,
                });

                if (response.data) {
                    dispatch({type: 'success', payload: response});
                }
            } catch (error) {
                dispatch({type: 'error', payload: {error: error.message}});
            }
        },
        [currentUser?.shopData?.idToken, pageSize, currentUser?.shopData?.sub_num]
    );

    useEffect(() => {
        if (!loading && currentUser === null) {
            navigate('/sign-in');
        }
    }, [currentUser, loading]);
    useEffect(() => {
        if (currentUser && currentUser.shopData) {
            fetchCallHistory();
        }
    }, [currentUser, fetchCallHistory]);
    // styles
    const baseStyles = {
        w: 7,
        fontSize: 'sm',
    };

    const normalStyles = {
        ...baseStyles,
        bg: 'grey.300',
    };

    const activeStyles = {
        ...baseStyles,
        // bg: "green.300",
        bg: 'rgba(70,97,137,1)',
        color: 'white',
    };

    const separatorStyles = {
        w: 7,
        bg: 'rgba(70,97,137,1)',
        color: 'white',
    };

    const onRequestCallBack = async () => {
        if (selectedCallGroup) {
            try {
                dispatch({type: 'submitting'});
                const response = await ApiFetch({
                    method: 'POST',
                    path: `/techcalls/request-callback`,
                    body: {
                        previousCallId: selectedCallGroup.id,
                        newComment: newCommentsRef?.current?.value ?? null,
                    },
                    userToken: currentUser?.shopData?.idToken,
                });

                if (response.code === 200) {
                    dispatch({type: "submitted"});
                } else if (response.errors && response.errors.length) {
                    dispatch({
                        type: "validationErrors", payload: {
                            validationErrors: response.errors
                        }
                    });
                }
            } catch (error) {
                dispatch({
                    type: "error", payload: {
                        error: error.message
                    }
                })
            } finally {
                dispatch({type: 'submitted'});
            }

            fetchCallHistory();
            onClose();
        }
    };

    const openRequestCallBackDetailOrFormModal = (call, renderRequestCallbackForm = true) => {
        setIsRequestCallbackModal(renderRequestCallbackForm);
        setSelectedCallGroup(call);
        onOpen();
    };

    React.useEffect(() => {
        setTimeout(() =>
                setRowWidth(tableRef?.current?.offsetWidth || 0)
            , [500])
    }, [tableRef?.current?.offsetWidth]);

    const getCallKey = (call) => [call.year, call.make, call.model, call.sub_num, call.trans, call.engine].join("__");

    const splitDirtyComments = (dirtyStr) => {
        if (!dirtyStr) return [];
        const separators = [/>>>/, /<<</, /Previous data:/, /\n/];
        let temp = [dirtyStr];
        separators.forEach(sep => {
            temp = temp.flatMap(str => str.split(sep));
        });
        return temp.map(s => s.replace(/[<>]/g, "").trim()).filter(Boolean);
    };

    const extractInvoiceNumber = (groupInvoiceFoundMap, groupKey, textToSearch) => {
        const NAH = "N/A";
        const regex = /\b(?:inv(?:oice)?)[\s#:]*?([A-Za-z0-9._\-]+)\b/gi;
        const matches = [...textToSearch.matchAll(regex)];
        const extractedInvoiceNumber = matches[0]?.[1] || NAH;

        groupInvoiceFoundMap[groupKey]['value'] = extractedInvoiceNumber !== NAH ? extractedInvoiceNumber : NAH;
    }

    const parseNameField = (name) => {
        if (!name) {
            return "N/A";
        }

        const upperCaseTechNameToTrimList = ["ED BEEDENBENDER", "ED BEEDENB"];
        const upperName = name.toUpperCase();

        if (upperCaseTechNameToTrimList.includes(upperName)) {
            return "ED B.";
        }

        return name;
    };

    const callsGroupedByVehicle = React.useMemo(() => {
        const sortedCalls = [...calls].sort((a, b) => a.incidentnum > b.incidentnum ? -1 : (a.incidentnum < b.incidentnum ? 1 : 0));
        const callGroups = {};
        let groupInvoiceFoundMap = {};

        sortedCalls.forEach(call => {
            let key = getCallKey(call);

            if (!callGroups[key]) {
                callGroups[key] = {
                    "calls": [],
                    "comments": [],
                    "currentComment": call.comments,
                    "year": call.year,
                    "make": call.make,
                    "model": call.model,
                    "engine": call.engine,
                    "trans": call.trans,
                    "sub_num": call.sub_num,
                };
            }

            const individualComments = splitDirtyComments(call.comments);

            const cleanedCommentMetaData = individualComments.map(text => {
                if (!(key in groupInvoiceFoundMap)) {
                    groupInvoiceFoundMap[key] = {value: "N/A"};
                }

                if (groupInvoiceFoundMap[key]['value'] === "N/A") {
                    extractInvoiceNumber(groupInvoiceFoundMap, key, text);
                }

                let mostRecentCallbackTime = call.takendate;
                let thisStcalltime = call.stcalltime ? call.stcalltime : "00:00:00";
                mostRecentCallbackTime = mostRecentCallbackTime + " " + thisStcalltime;

                return {
                    cleanedComment: text,
                    invoiceNumber: groupInvoiceFoundMap[key]['value'],
                    technam: parseNameField(call.technam),
                    takenby: parseNameField(call.takenby),
                    takentime: call.takentime,
                    takendate: call.takendate,
                    stcalltime: call.stcalltime,
                    assignedto: parseNameField(call.assignedto),
                    callDatetime: mostRecentCallbackTime,
                    status: parseNameField(call.status),
                    atsgtechreq: parseNameField(call.atsgtechreq),
                    probstring: call.probstring,
                    call: call,
                    id: call.id,
                };
            });

            callGroups[key].calls.push(call);
            for (let k = 0; k < cleanedCommentMetaData.length; k++) {
                callGroups[key].comments.push(cleanedCommentMetaData[k]);
            }
        });

        const newCallGroups = [];
        Object.keys(callGroups).forEach(callGroupKey => {
            const callGroup = callGroups[callGroupKey];
            callGroup.comments.sort((a, b) => a.callDatetime > b.callDatetime ? -1 : (a.callDatetime < b.callDatetime ? 1 : 0));

            const groupCommentSample = callGroup.comments[0];

            callGroup.mostRecentCallTime = groupCommentSample.callDatetime;
            callGroup.technam = parseNameField(groupCommentSample.technam) || 'N/A';
            callGroup.assignedto = parseNameField(groupCommentSample.assignedto) || 'N/A';
            callGroup.takenby = parseNameField(groupCommentSample.takenby) || 'N/A';
            callGroup.atsgtechreq = parseNameField(groupCommentSample.atsgtechreq) || 'N/A';
            callGroup.atsgtechreq = groupCommentSample.atsgtechreq;
            callGroup.status = groupCommentSample.status;
            callGroup.probstring = groupCommentSample.probstring;
            callGroup.takendate = groupCommentSample.takendate;
            callGroup.takentime = groupCommentSample.takentime;
            callGroup.invoiceNumber = groupCommentSample.invoiceNumber;
            callGroup.id = groupCommentSample.id;

            newCallGroups.push(callGroup);
        })
        newCallGroups.sort((a, b) => a.mostRecentCallTime > b.mostRecentCallTime ? -1 : (a.mostRecentCallTime < b.mostRecentCallTime ? 1 : 0));
        return newCallGroups;

    }, [calls]);


    const Field = ({label, value}) => (
        <Box
            pb={1}
            mb={1}
            borderBottom="1px solid rgba(56, 71, 94, 1.00)"
        >
            <Text fontSize="md" fontWeight="black" color="rgba(56, 71, 94, 1.00)" mb={1}>
                {label}
            </Text>
            <Text fontSize="md" color="gray.800" wordBreak="break-word">
                {value || "NA"}
            </Text>
        </Box>
    );

    const DetailsModalComment = () => {
        if (!selectedCallGroup) {
            return null;
        }

        const comments = selectedCallGroup.comments || [];
        const commentsByDatetime = [];
        let prevDatetime = null;

        comments.forEach(commentData => {
            if (!commentData.cleanedComment) {
                return;
            }
            const headerKey = commentData.callDatetime;
            if (headerKey !== prevDatetime) {
                commentsByDatetime.push([]);
            }
            commentsByDatetime[commentsByDatetime.length - 1].push(commentData);
            prevDatetime = headerKey;
        });

        return (
            <Box
                width="100%"
                borderBottom="2px solid #ccc"
                mb="80px"
                mt="20px"
            >
                {commentsByDatetime.length > 0 ? (
                    commentsByDatetime
                        .map((commentListData, index) => (
                            <Box key={commentListData[0].id}>
                                <Box mb={6} mt={6} pr={5}>
                                    <Flex justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" mb={1}>
                                        <Text fontWeight="bolder" color="rgba(56, 71, 94, 1.00)" fontSize="sm">
                                            Call Date: {commentListData[0].callDatetime}
                                        </Text>
                                        <Box>
                                            <Text fontWeight="bolder" color="rgba(56, 71, 94, 1.00)" bg="blue.300" fontSize="sm">
                                                Taken By: {commentListData[0].takenby ? commentListData[0].takenby.toUpperCase() : "N/A"} - Tech
                                                Name: {commentListData[0].technam ? commentListData[0].technam.toUpperCase() : "N/A"}
                                            </Text>
                                        </Box>
                                        <Text fontWeight="bolder" color="rgba(56, 71, 94, 1.00)" fontSize="sm">
                                            Assigned To: {commentListData[0].assignedto ? commentListData[0].assignedto.toUpperCase() : "N/A"}
                                        </Text>
                                    </Flex>
                                        <hr/>

                                    <List spacing={1}>
                                        {commentListData.map((commentData, index) => (
                                            <ListItem
                                                key={index}
                                                bg="blue.200"
                                                borderRadius="4px"
                                                pl={4}
                                                py={1}
                                            >
                                                <ListIcon as={ChevronRightIcon} color="blue.500" p={0} si title='commentData.'/>
                                                <Text color="gray.700" fontSize="sm" whiteSpace="pre-wrap" display="inline">
                                                    {commentData.cleanedComment}
                                                </Text>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            </Box>

                        ))
                ) : (
                    <Text>No comments to display</Text>
                )}
            </Box>

        );
    };

    const showLoaderPage = useNeedsMembership()
    if (showLoaderPage) {
        return <Loader/>;
    }

    if (!calls) {
        return <Loader/>;
    }

    return (
        <Box style={{width: "85%", margin: "0 auto"}}>
            {status === STATUSES.ERROR && <div>An error happened {error}</div>}
            <div
                ref={fakeScrollRef}
                onScroll={onScrollFake}
                style={{
                    width: `100%`,
                    overflowX: "auto",
                    overflowY: "hidden",
                    padding: "0px",
                    margin: "0",
                }}
            >
                <div style={{
                    width: `${rowWidth}px`,
                    height: "20px"
                }} id="internal-fake-scroll-div">
                </div>
            </div>

            <div
                ref={containerRef}
                onScroll={onScrollReal}
                style={{
                    width: "100%",
                    margin: "0",
                    display: "flex",
                    flexDirection: "column",
                    overflowX: "auto",
                    overflowY: "hidden",
                    padding: "0px"
                }}
            >

                <Table variant="simple" ref={tableRef} id="table">
                    <Thead>
                        <Tr
                            ref={rowRef}
                            sx={{
                                th: {
                                    fontSize: 14,
                                    bg: 'light',
                                    color: 'darkGrey',
                                    width: 'auto',
                                    borderRight: '1px solid #ddd',
                                    borderLeft: '1px solid #ddd',
                                },
                            }}
                        >
                            {isRecycler && (<Th>Invoice</Th>)}
                            <Th>Request Callback</Th>
                            <Th>Vehicle</Th>
                            <Th>Shop Tech</Th>
                            <Th>ATSG Tech Requested</Th>
                            <Th>Most Recent Call</Th>
                            <Th>Current Status</Th>
                            <Th>View Details</Th>
                            <Th>Problem Description</Th>
                        </Tr>
                    </Thead>
                    <Tbody role="rowgroup">
                        {callsGroupedByVehicle.map((callGroup) => (
                            <Tr
                                key={callGroup.id}
                                sx={{
                                    td: {
                                        borderRight: '1px solid #ddd',
                                        borderLeft: '1px solid #ddd',
                                        fontFamily: 'heading',
                                        fontWeight: '400',
                                        minHeight: '100%',
                                        paddingBottom: '0',
                                    },
                                }}
                            >
                                {isRecycler && (<Td>{callGroup.invoiceNumber}</Td>)}
                                <Td>
                                    <Button
                                        variant="lightBlue"
                                        width="auto"
                                        fontWeight="bold"
                                        onClick={() => openRequestCallBackDetailOrFormModal(callGroup, true)}
                                    >
                                        CALLBACK
                                    </Button>
                                </Td>
                                <Td>{callGroup.year} {callGroup.make ? callGroup.make.toUpperCase() : 'N/A'} {callGroup.model ? callGroup.model.toUpperCase() : "N/A"} {callGroup.engine ? callGroup.engine.toUpperCase() : "N/A"} {callGroup.trans ? callGroup.trans.toUpperCase() : "N/A"}</Td>
                                <Td>{callGroup.technam ? callGroup.technam.toUpperCase() : 'N/A'}</Td>
                                <Td>{callGroup.atsgtechreq ? callGroup.atsgtechreq.toUpperCase() : 'N/A'}</Td>
                                <Td>{callGroup.mostRecentCallTime ? callGroup.mostRecentCallTime.toUpperCase() : "N/A"}</Td>
                                <Td>{callGroup.status ? callGroup.status.toUpperCase() : "N/A"}</Td>
                                <Td>
                                    <Button
                                        variant="lightBlue"
                                        width="auto"
                                        fontWeight="bold"
                                        onClick={() => openRequestCallBackDetailOrFormModal(callGroup, false)}
                                    >
                                        Details
                                    </Button>
                                </Td>
                                <Td>
                                    <Box
                                        maxH="200px"
                                        width="280px"
                                        overflowY="auto"
                                        fontSize="sm"
                                        color="gray.800"
                                    >
                                        {callGroup.probstring?.replace(/[<>]/g, "\n") || ""}
                                    </Box>
                                </Td>

                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </div>

            <div
                ref={containerRef}
                onScroll={onScrollReal}
                style={{
                    width: "100%",
                    margin: "0",
                    display: "flex",
                    flexDirection: "column",
                    overflowX: "auto",
                    overflowY: "hidden",
                    padding: "0px"
                }}
            >
            </div>

            <Container className="mt-10">
                <ChakraProvider>
                    <Paginator
                        isDisabled={pageCount === 1}
                        currentPage={page}
                        pagesQuantity={pageCount}
                        onPageChange={onGoToPage}
                        activeStyles={activeStyles}
                        innerLimit={1}
                        outerLimit={2}
                        normalStyles={normalStyles}
                        separatorStyles={separatorStyles}
                    >
                        <Flex justify="space-between" pb={5}>
                            <Previous bg="rgba(70,97,137,1)" color="white">
                                Previous
                            </Previous>
                            <PageGroup isInline align="center"/>
                            <Next bg="rgba(70,97,137,1)" color="white">
                                Next
                            </Next>
                        </Flex>
                    </Paginator>
                </ChakraProvider>
            </Container>
            <Modal isOpen={isOpen} onClose={onClose} size="6xl" he scrollBehavior="inside">
                <ModalOverlay/>
                <ModalContent>
                    <ModalHeader className="font-bold text-white bg-blue text-md font-heading">{isRequestCallbackModal ? 'Request A Callback' : 'Callback Details'}</ModalHeader>
                    <ModalCloseButton/>
                    <ModalCloseButton color="white"/>
                    <ModalBody
                        overflowY="auto"
                        maxH="85vh"
                        pr={5}
                    >
                        {selectedCallGroup && (
                            <>
                                <Grid templateColumns={{base: "1fr", md: "repeat(3, 1fr)"}} gap={6} mb={2}>
                                    {isRecycler && (<Field label="Invoice Number" value={selectedCallGroup.invoiceNumber}/>)}
                                    <Field label="Current Status" value={selectedCallGroup.status ? selectedCallGroup.status.toUpperCase() : "N/A"}/>
                                    <Field label="Year" value={selectedCallGroup.year}/>
                                    <Field label="Make" value={selectedCallGroup.make ? selectedCallGroup.make.toUpperCase() : "N/A"}/>
                                    <Field label="Model" value={selectedCallGroup.model ? selectedCallGroup.model.toUpperCase() : "N/A"}/>

                                    <Field label="Engine" value={selectedCallGroup.engine ? selectedCallGroup.engine.toUpperCase() : "N/A"}/>
                                    <Field label="Trans Type" value={selectedCallGroup.trans ? selectedCallGroup.trans.toUpperCase() : "N/A"}/>

                                    <Field label="Shop Tech Name" value={selectedCallGroup.technam ? selectedCallGroup.technam.toUpperCase() : "N/A"}/>
                                    <Field label="Most Recent Call" value={selectedCallGroup.mostRecentCallTime}/>

                                    <Field label="ATSG Tech Assigned" value={selectedCallGroup.atsgtechreq ? selectedCallGroup.atsgtechreq.toUpperCase() : "N/A"}/>
                                </Grid>
                            </>
                        )}
                        {(isRequestCallbackModal && selectedCallGroup) && (
                            <Box width="100%" mt={8}>

                                <Text
                                    fontSize="md"
                                    fontWeight="black"
                                    color="rgba(56, 71, 94, 1.00)"
                                    mb={2}
                                >
                                    Add Comment
                                </Text>

                                <FormElement
                                    id="comments"
                                    label=""
                                    sx={{
                                        mt: 4,
                                        width: "100%",
                                        "& label": {display: "none"},
                                    }}
                                >
                                    <Textarea
                                        ref={newCommentsRef}
                                        placeholder="Comments"
                                        height="154px"
                                        width="150%"
                                        resize="vertical"
                                    />
                                </FormElement>
                            </Box>
                        )}
                        <Box maxW={"100%"} borderBottom="2px solid rgba(56, 71, 94, 1.00)">
                            <Text fontSize="md" fontWeight="black" mt={6} color="rgba(56, 71, 94, 1.00)" mb={1}>
                                Comment History
                            </Text>
                        </Box>
                        {selectedCallGroup && (
                            <Box
                                maxH="220px"
                                overflowY="auto"
                                border={selectedCallGroup.comments.length > 0 ? "0px 2px 2px 2px solid rgba(56, 71, 94, 0.2)" : ""}
                                borderRadius="md"
                                mt={1}
                            >
                                <DetailsModalComment/>
                            </Box>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Flex justify="space-between" width="100%">
                            <Button
                                variant="gray.800"
                                width="auto"
                                bg="gray.600"
                                onClick={onClose}
                            >
                                Close
                            </Button>
                            {isRequestCallbackModal && status !== STATUSES.SUBMITTING && (

                                  <Button
                                    variant="lightBlue"
                                    width="auto"
                                    mr={3}
                                    onClick={onRequestCallBack}
                                  >
                                    Submit
                                  </Button>
                                )}
                                {status === STATUSES.SUBMITTING && (
                                  <Button
                                    variant="lightBlue"
                                    width="auto"
                                    mr={3}
                                    isLoading
                                    loadingText="submitting"
                                  >
                                    Submit
                                  </Button>
                                )}
                        </Flex>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    )
};


export default CallHistoryPage;
